import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Button, ButtonType, Form, FormGroup, Input, Select, SubmitButton } from '@digicert/dcone-common-ui'
import { translate } from '../../../../../shared/helpers/utils'
import { NAME_REGEXP } from '../../../../../core/utils/constants'
import { Translate } from 'react-localize-redux'
import styles from './EditOverView.module.scss'
import { IAppState } from '../../../../../core/store/store'
import { EditOverViewProps } from './EditOverView.types'
import { editTenant } from '../../../Tenants.actions'
import { ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { Moment } from 'moment/moment'
import moment from 'moment'
import { IAccessScope } from '../../../../../reducers/Reducers.interfaces';

const EditOverView = (
    {
        account,
        localeList,
        onCancel,
        setAccount,
        updateNav,
        accessScope
    }: EditOverViewProps):ReactElement => {
    const dispatch: ThunkDispatch<IAppState, any, AnyAction> = useDispatch()
    const [accountName, setAccountName] = useState(account?.name)
    const [nameMaxLengthError, setNameMaxLengthError] = useState(false)
    const [localeSelection, setLocaleSelection] = useState(account?.locale)
    const validate = () => (!localeSelection ? translate('form.validation.invalidSelect') as string : '')
    const locales = useSelector(() => localeList?.locales.map(item => ({
        value: item.id,
        label: item.name
    })))
    const [exactDate, setExactDate] = useState<Moment | null>(null)
    const [applications, setApplications] = useState<any>()

    useEffect(() => {
      if (account) {
        setAccountName(account.name)
        setLocaleSelection(account.locale)
        setExactDate(moment(account.service_period.to))
        setApplications(account.applications.map(app => {
          return app.id
        }))
      }
    }, [account, onCancel])

    const changeName = (e: ChangeEvent<HTMLInputElement>) => {
        if (e) {
            const name = e.target.value
            setAccountName(name)
            if (name.length > 255) {
                setNameMaxLengthError(true)
            } else {
                setNameMaxLengthError(false)
            }
        }
    }

    const nameValidity = useCallback(() => {
        if (nameMaxLengthError) {
            return translate('notifications.errors.maxLengthError') as string
        }
    }, [nameMaxLengthError])

    const handleSelect = (value): void => {
        setLocaleSelection(value)
    }

    const handleCancel = (e) => {
        e.preventDefault()
        if(onCancel) {
            onCancel()
        }
    }

    const onSubmit = (e) => {
        e.preventDefault()
        const message = <>
            {translate('notifications.accounts.accountUpdated') as string} <strong>{accountName}.</strong>
        </>
        if(!account) return
        const postData = {
            name: accountName,
            locale: localeSelection,
            'service_period': {
                from: account.service_period.from,
                to: exactDate
            },
            applications: applications
        }

        dispatch(editTenant(account.id, postData, accessScope, message)).then((res) => {
            setAccount(res)
            updateNav(res)
            if(onCancel) {
                onCancel()
            }
        })
    }

    return (
        <section className={styles.editOverView}>
            <Form center={false} onSubmit={onSubmit}>
                <FormGroup>
                    <Input
                        disabled={accessScope === IAccessScope.account}
                        readOnly={accessScope === IAccessScope.account}
                        label={translate('createAccount.accountName') as string}
                        required
                        minLength={1}
                        maxLength={256}
                        value={accountName}
                        onChange={changeName}
                        visibilityToggle={true}
                        errors={{
                            valueMissing: translate('common.form.validation.required') as string
                        }}
                        id='account_name'
                        validate={nameValidity}
                    />
                </FormGroup>

                <FormGroup>
                    <Select
                        allowClear
                        validate={validate}
                        defaultValue={localeSelection}
                        value={localeSelection}
                        onChange={handleSelect}
                        options={locales}
                        label={translate('profile.language') as string}
                    />
                </FormGroup>

                <div className={styles.actions}>
                    <Button buttonType={ButtonType.SECONDARY} onClick={handleCancel}>
                        <Translate id='common.form.buttons.cancelBtn'/>
                    </Button>

                    <SubmitButton disabled={!accountName}>
                        <Translate id='common.form.buttons.updateAccount'/>
                    </SubmitButton>
                </div>
            </Form>
        </section>
    )
}

export default connect(
    (state: IAppState) => ({
        profile: state.profile?.data,
        localeList: state.locales
    }),
    null
)(EditOverView)
